import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Store } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useUpdateStore } from '@/features/stores';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { feedKeys } from '../hooks/feed-keys';
import {
  GOOGLE_PRODUCT_CATEGORY_MAX,
  feedSettingsSchema,
  fromFeedSettings,
  toFeedSettingsPatch,
  type FeedSettingsForm,
} from '../schemas/feed-settings-schema';

const mapField = (field: string) => field.replace(/^settings\.feeds\./, '');
const KNOWN_FIELDS: ReadonlySet<string> = new Set(['googleProductCategory', 'includeOutOfStock']);

/**
 * The editable part of `settings.feeds`, saved through the store PATCH (which needs
 * `settings.write`). The item counts depend on it, so the feed status is refetched after a save.
 */
export function FeedSettingsCard({ store }: { store: Store }) {
  const { t } = useTranslation(['feeds', 'common']);
  const readOnly = !useStoreEditable({ store, permission: 'settings.write' }).editable;
  const queryClient = useQueryClient();
  const update = useUpdateStore(store.id, 'feeds:toasts.settingsSaved');
  const message = useErrorMessage();
  const form = useForm<FeedSettingsForm>({
    resolver: useI18nResolver(feedSettingsSchema),
    defaultValues: fromFeedSettings(store),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toFeedSettingsPatch(values));
      form.reset(values);
      await queryClient.invalidateQueries({ queryKey: feedKeys.all(store.id) });
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return (
    <FormSection
      title={t('feeds:settings.title')}
      description={t('feeds:settings.description')}
      id={SECTION_ANCHORS.feedSettings}
      tour="settings.feeds.settings"
    >
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('feeds:settings.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="googleProductCategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('feeds:settings.category')}</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="off"
                    maxLength={GOOGLE_PRODUCT_CATEGORY_MAX}
                    placeholder="Apparel & Accessories > Jewelry > Rings"
                    disabled={readOnly}
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t('feeds:settings.categoryHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="includeOutOfStock"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start gap-3">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={readOnly}
                    className="mt-0.5"
                  />
                </FormControl>
                <div className="grid gap-1">
                  <FormLabel>{t('feeds:settings.includeOutOfStock')}</FormLabel>
                  <FormDescription>{t('feeds:settings.includeOutOfStockHint')}</FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
          {readOnly ? null : (
            <div>
              <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
                {update.isPending ? t('common:actions.saving') : t('feeds:settings.submit')}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </FormSection>
  );
}
